import { Button } from '@/components/ui/button'
import { Download, Loader2 } from 'lucide-react'
import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import datacentral from '@/utils/axios/datacentral'
import queryString from 'query-string'
import { useModal } from '@/contexts/modal'
import { ColumnMapping, ActiveFilter } from '@/components/ExcelExportModal'

// Re-export types for convenience
export type { ColumnMapping, ActiveFilter }

export interface ExcelExportConfig {
    /** API endpoint (e.g., "employees") */
    apiEndpoint: string
    /** Available columns for export */
    columns: ColumnMapping[]
    /** Query params from useUrlSearchParams - will add export=excel automatically */
    queryParams: Record<string, any>
    /** Feature name for modal title */
    feature?: string
    /** Button text */
    buttonText?: string
    /** Disable export */
    disabled?: boolean
    /** Active filters to display in modal */
    activeFilters?: ActiveFilter[]
}

interface ExcelExportButtonProps extends ExcelExportConfig {
    /** Button variant */
    variant?: 'default' | 'outline' | 'ghost' | 'link'
    /** Additional className */
    className?: string
}

/**
 * ExcelExportButton - Standalone export button with modal
 * Can be used in DataTable settings slotComponents or anywhere else
 *
 * @example
 * <ExcelExportButton
 *     apiEndpoint="employees"
 *     columns={EMPLOYEE_EXCEL_COLUMNS}
 *     queryParams={queryParams}
 *     feature="employee"
 * />
 */
export function ExcelExportButton({
    apiEndpoint,
    columns,
    queryParams,
    feature,
    buttonText,
    disabled,
    activeFilters,
    variant = 'ghost',
    className = '',
}: ExcelExportButtonProps) {
    const { t } = useTranslation('data-table')
    const { setModal } = useModal()
    const [isExporting, setIsExporting] = useState(false)

    const handleExport = useCallback(
        async (selectedColumns: ColumnMapping[], filename: string) => {
            try {
                setIsExporting(true)

                // Merge queryParams with export flag
                const params = {
                    ...queryParams,
                    export: 'excel',
                    columns: JSON.stringify(selectedColumns),
                    ...(filename && { filename: `${filename}.xlsx` }),
                }

                // Call API
                const response = await datacentral.get(
                    `/${apiEndpoint.replace(/\/+$/, '')}?${queryString.stringify(params)}`,
                    { responseType: 'blob' }
                )

                // Create download link
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                })
                const downloadUrl = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = downloadUrl
                link.download = filename ? `${filename}.xlsx` : 'export.xlsx'
                document.body.appendChild(link)
                link.click()

                // Cleanup
                window.URL.revokeObjectURL(downloadUrl)
                document.body.removeChild(link)

                toast.success(t('export.success'))
            } catch (error) {
                console.error('Export error:', error)
                toast.error(t('export.error'))
            } finally {
                setIsExporting(false)
            }
        },
        [apiEndpoint, queryParams, t]
    )

    const handleOpenModal = () => {
        setModal('ExcelExport/index', {
            onExport: handleExport,
            availableColumns: columns,
            feature,
            activeFilters,
        })
    }

    return (
        <Button
            variant={variant}
            onClick={handleOpenModal}
            disabled={disabled || isExporting}
            className={`gap-2 w-full justify-start ${className}`}
        >
            {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
                <Download className="h-4 w-4" />
            )}
            {isExporting
                ? t('export.exporting')
                : buttonText || t('export.excel')}
        </Button>
    )
}
